import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, AlertCircle, Chrome } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { cx } from "@/components/site/primitives";
import { useAuth } from "@/lib/useAuth";
import { getPostAuthDestination } from "@/lib/authRedirect";
import { LOGIN } from "@/constants/testIds";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALID_EMAIL = "sakshi@gmail.com";
const VALID_PASSWORD = "Sakshi@123";

export default function SignInForm() {
  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [resetOpen, setResetOpen] = useState(false);
  const [resetValues, setResetValues] = useState({ token: "", newPassword: "" });
  const [resetStatus, setResetStatus] = useState("idle");
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");

  const validate = () => {
    const next = {};
    if (!email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address.";
    if (!password) next.password = "Password is required.";
    else if (password.length < 6) next.password = "Password must be at least 6 characters.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!validate() || status === "loading") return;

    setStatus("loading");
    try {
      await signIn({ email: email.trim(), password, remember });
      setStatus("success");
      window.setTimeout(() => {
        navigate(getPostAuthDestination({ from: location.state?.from }), { replace: true });
      }, 420);
    } catch (error) {
      setStatus("idle");
      setAuthError(error.message || "Incorrect email or password. Give it another try.");
    }
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setResetError("");
    setResetSuccess("");
    if (!resetValues.token.trim() || !resetValues.newPassword) {
      setResetError("Token and new password are required.");
      return;
    }
    if (resetValues.newPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }

    setResetStatus("loading");
    try {
      await resetPassword({ token: resetValues.token.trim(), newPassword: resetValues.newPassword });
      setResetStatus("idle");
      setResetSuccess("Your password has been reset. You can sign in now.");
      setResetValues({ token: "", newPassword: "" });
    } catch (error) {
      setResetStatus("idle");
      setResetError(error.message || "We could not reset your password. Please try again.");
    }
  };

  const isLoading = status === "loading";
  const isSuccess = status === "success";

  return (
    <motion.form
      onSubmit={handleSubmit}
      noValidate
      initial={{ opacity: 0 }}
      animate={{ opacity: isSuccess ? 0 : 1, scale: isSuccess ? 0.98 : 1 }}
      transition={{ duration: 0.35 }}
      className="w-full"
      data-testid="signin-form"
    >
      <AnimatePresence>
        {authError && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.25 }}
            className="mb-4 flex items-start gap-2.5 rounded-xl border border-coral/30 bg-coral/10 px-4 py-3 text-sm text-coral"
            role="alert"
            data-testid="signin-error-banner"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{authError}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-4">
        <div>
          <Label htmlFor="signin-email" className="text-ink">Email</Label>
          <Input
            id="signin-email"
            type="email"
            autoComplete="email"
            placeholder="you@scribblereads.app"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
            aria-invalid={!!errors.email}
            data-testid={LOGIN.emailInput}
            className={cx(
              "mt-1.5 h-11 rounded-xl border-line bg-cream/70 px-4 text-[15px] text-ink placeholder:text-subtle/70 transition-colors focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral",
              errors.email && "border-coral focus-visible:ring-coral"
            )}
          />
          {errors.email && <p className="mt-1.5 text-xs font-medium text-coral">{errors.email}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="signin-password" className="text-ink">Password</Label>
            <button
              type="button"
              onClick={() => { setResetError(""); setResetSuccess(""); setResetOpen(true); }}
              data-testid={LOGIN.forgotPasswordLink}
              className="link-underline text-xs font-semibold text-coral"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative mt-1.5">
            <Input
              id="signin-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((p) => ({ ...p, password: undefined })); }}
              aria-invalid={!!errors.password}
              data-testid={LOGIN.passwordInput}
              className={cx(
                "h-11 rounded-xl border-line bg-cream/70 px-4 pr-11 text-[15px] text-ink placeholder:text-subtle/70 transition-colors focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral",
                errors.password && "border-coral focus-visible:ring-coral"
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-subtle transition-colors hover:text-coral"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="mt-1.5 text-xs font-medium text-coral">{errors.password}</p>}
        </div>

        <div className="flex items-center justify-between pt-1">
          <label className="flex select-none items-center gap-2 text-sm text-subtle">
            <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-line data-[state=checked]:bg-coral data-[state=checked]:border-coral" />
            Remember me
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          data-testid={LOGIN.submitButton}
          className="group relative flex h-12 w-full items-center justify-center gap-2 rounded-full bg-coral text-[15px] font-semibold text-[#FFF7F0] shadow-soft transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-80 disabled:hover:translate-y-0"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
            </>
          ) : (
            "Sign In"
          )}
        </button>
      </div>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <span className="text-xs font-medium uppercase tracking-wider text-subtle">or</span>
        <span className="h-px flex-1 bg-line" />
      </div>

      <button
        type="button"
        onClick={() => toast("Google sign-in is coming soon.")}
        data-testid="signin-google"
        className="flex h-12 w-full items-center justify-center gap-2.5 rounded-full border border-line bg-card text-[15px] font-semibold text-ink transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:border-coral/50 hover:shadow-soft"
      >
        <Chrome className="h-4 w-4" />
        Continue with Google
      </button>

      <p className="mt-6 text-center text-sm text-subtle">
        {/* New to ScribbleReads?{" "}
        <Link
          to="/signup"
          data-testid={LOGIN.registerLink}
          className="link-underline font-semibold text-coral"
        >
          Create an account
        </Link> */}
        Coming soon...
      </p>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="font-display text-2xl">Reset your password</DialogTitle>
          <DialogDescription className="text-sm text-subtle">Enter the reset token from your password-reset email.</DialogDescription>
          <form onSubmit={handleResetSubmit} onClick={(event) => event.stopPropagation()} className="mt-2 space-y-4" noValidate>
            {resetError && <p className="rounded-xl border border-coral/30 bg-coral/10 px-3 py-2 text-sm text-coral" role="alert">{resetError}</p>}
            {resetSuccess && <p className="rounded-xl border border-sage/40 bg-sage/20 px-3 py-2 text-sm text-ink" role="status">{resetSuccess}</p>}
            <div>
              <Label htmlFor="reset-token" className="text-ink">Reset token</Label>
              <Input id="reset-token" value={resetValues.token} onChange={(event) => setResetValues((current) => ({ ...current, token: event.target.value }))} className="mt-1.5 h-11 rounded-xl border-line bg-cream/70" autoComplete="one-time-code" />
            </div>
            <div>
              <Label htmlFor="reset-new-password" className="text-ink">New password</Label>
              <Input id="reset-new-password" type="password" value={resetValues.newPassword} onChange={(event) => setResetValues((current) => ({ ...current, newPassword: event.target.value }))} className="mt-1.5 h-11 rounded-xl border-line bg-cream/70" autoComplete="new-password" />
            </div>
            <button type="submit" disabled={resetStatus === "loading"} className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-coral text-sm font-semibold text-[#FFF7F0] disabled:opacity-70">
              {resetStatus === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Resetting...</> : "Reset password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </motion.form>
  );
}
